/**
 * Iris's per-user profile outside the Settings page (CU-869fkmt6r): what decides the launcher's
 * "Set up Iris", the widget's welcome and what travels to Iris on every question. Pure — no React,
 * no signals, no palette, no `window` — so `node --test` runs it.
 *
 * Server contract (`GET/PUT /api/ia-workspace/profile`): `profile` = the editable shape of
 * `EMPTY` (`rentacar-webback/app/blueprints/ia_workspace/services/profile.py`) plus, computed and
 * never stored, `own` and `compare: {axis} | null`. `own` (CU-869fmj51h) is a brand's
 * `{kind: 'provider', id, label[, sites]}` or a broker's `{kind: 'site', sites}` (no id nor label), with
 * `sites: [{id, label, country, country_name}]`, or null. The front only passes it on.
 * Ported from the workspace's `src/profile/signals.js` (`hasProfile`, `chatProfile`).
 */
// relative, not `@components`: `node --test` does not resolve the build's aliases (TL R4)
import { sortSites } from '../@components/IdentityEditor/@services/identity.js'

/** Something of the user's own is saved; the identity (`own` / `compare`) never counts (criterion 14c). */
export function hasEditableProfile(profile) {
	if (!profile) return false
	const f = profile.focus ?? {}
	const c = f.channels ?? {}
	return Boolean(
		profile.lang || profile.target_rank || profile.voice?.preset || profile.voice?.notes
		|| profile.compset?.tier || profile.compset?.provider_ids?.length
		|| f.offices?.length || f.lors?.length || f.acriss_classes?.length || f.acriss_list?.length
		|| c.site_ids?.length || c.kind || c.group,
	)
}

/**
 * What `DSB.setContext({ profile })` carries: the profile as the server answered (with `own` /
 * `compare`) plus the server's text. `undefined` when there is nothing of the user's and no identity
 * (criterion 18); with an identity and nothing else it still travels (criterion 20).
 */
export function chatProfile(profile, prompt) {
	if (!profile) return undefined
	if (!hasEditableProfile(profile) && !profile.own) return undefined
	return { ...profile, prompt: prompt || undefined }
}

/**
 * Hands the profile to the widget (0.109.4+). Feature-detected: a widget without `setContext` is left
 * alone and `false` comes back. `profile === null`, or nothing to send, sends `{ profile: null }`,
 * which drops what the widget held (logout, criterion 18b).
 */
export function pushIrisProfile(dsb, profile, prompt) {
	if (!dsb || typeof dsb.setContext !== 'function') return false
	const next = profile ? chatProfile(profile, prompt) : undefined
	dsb.setContext({ profile: next ?? null })
	return true
}

const AXIS_LABELS = { brand: 'Brand vs brand', site: 'Site vs site' }
// Sites shown in "Who you are" before "and N more — see Settings › Account" (UX)
export const WHO_SITES_PREVIEW = 6

/**
 * "Who you are", the read-only mirror of Settings › Account (`identity` as `account.js` holds it:
 * `{ kind: 'brand' | 'broker', providers: [{id, label}], sites: [{id, label, market, market_name}] }`).
 * The axis is the profile's `compare` when the server sent one, else the identity's kind. Sites have no
 * primary: the first `WHO_SITES_PREVIEW` in the editor's order (`sortSites`) and how many more.
 */
export function identityView(identity, compare) {
	const kind = identity?.kind ?? null
	const providers = identity?.providers || []
	const sites = identity?.sites || []
	if (!identity || (!kind && !providers.length && !sites.length)) return { hasIdentity: false }

	const axis = compare?.axis ?? (kind === 'broker' ? 'site' : kind === 'brand' ? 'brand' : null)
	const brands = kind === 'broker' ? [] : providers.map((p, i) => ({ id: p.id, label: p.label ?? null, primary: i === 0 }))
	const sorted = sortSites(sites)
	return {
		hasIdentity: true,
		kind,
		kindLabel: kind === 'broker' ? 'Broker' : 'Brand',
		brands,
		sites: sorted.slice(0, WHO_SITES_PREVIEW),
		moreSites: Math.max(0, sorted.length - WHO_SITES_PREVIEW),
		axis,
		axisLabel: axis ? (AXIS_LABELS[axis] ?? String(axis)) : null,
	}
}

export const IRIS_SETTINGS_PATH = '/user/iris'

/** UX D16, 33 words in Iris's voice — the drawer's first message while the user has saved nothing. */
export const SETUP_WELCOME = (path = IRIS_SETTINGS_PATH) =>
	`I work better once I know who you are and what you care about — your language, your tone, who you compare against, your offices. [Set up Iris in Settings › Iris](${path}). Until then, ask me anything.`

/**
 * `data-welcome` for the widget, read once at mount: `null` (the agent's own welcome) when a profile
 * of the user's is loaded; the set-up welcome otherwise — nothing saved, a failed load, or still
 * loading when the wait ran out (criteria 14b, 14c).
 */
export function welcomeMarkdown({ profile, state, settingsPath = IRIS_SETTINGS_PATH } = {}) {
	if (profile && state !== 'error' && hasEditableProfile(profile)) return null
	return SETUP_WELCOME(settingsPath)
}

/**
 * `data-lang` for the widget, read once at mount (CU-869fkdhjv, Curro 2026-10-28): the saved Iris language,
 * so the agent's welcome and starter suggestions come in it (the widget asks its server for them translated).
 * `null` when Not set or not loaded: no `data-lang`, Iris mirrors the user. A saved language is a saved
 * profile, so `welcomeMarkdown` is `null` then — our English set-up welcome never travels with a `data-lang`.
 */
export function widgetLang(profile) {
	const lang = typeof profile?.lang === 'string' ? profile.lang.trim().toLowerCase() : ''
	return lang || null
}
